// A preference that belongs to THIS BROWSER, and how it is remembered.
//
// The look (`theme.ts`) and wording (`voice.ts`) are per-device preferences by
// decision rather than omission - dark on a phone at night and light on a
// desktop is the right behaviour, not a compromise. The daily notification also
// uses this storage for its bounded endpoint locator; the setting itself remains
// a server row because the server cannot send without it. This is the shared
// storage mechanism, written once.
//
// It is deliberately not a general key-value store: it reads and writes strings,
// swallows every failure, and knows nothing about what a value means. A
// vocabulary and a default are the caller's, because only the caller has them -
// `readChoice` below takes both as arguments and never learns what a theme or a
// wording is. What it owns is the ORDER the three answers are tried in, which is
// the one part every per-browser preference shares and the one part that must
// not be spelled out again per preference.

/**
 * A year, in seconds, for the cookie mirror.
 *
 * WHY THERE IS A COOKIE AT ALL, given `localStorage` is tried first:
 * script-written storage is capped at seven days by Safari's tracking
 * prevention, and a `SameSite=Lax` cookie is not. A person who opens the app
 * fortnightly would otherwise find their preferences reset each time, which
 * looks exactly like a bug and is not one. It is also the mirror
 * `web-architecture.md` §9.2 names, and the hook the account-level preference
 * would arrive on - see the note in `web/src/README.md`.
 */
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * `localStorage` first, then the cookie mirror. NEITHER MAY THROW OUT OF HERE.
 *
 * Storage can be disabled outright, in which case reading it raises rather than
 * answering null - and `main.tsx` reads a preference before the app renders, so
 * an escaping error would trade a cosmetic choice for a blank page. The fallback
 * is the caller's default, which is a look and a wording.
 *
 * TWO CATCHES RATHER THAN ONE, and the pre-paint copy in `index.html` has two
 * for the same reason: a cookie jar that raises costs THIS KEY, not the answer.
 * One catch around both reads would lose the mode because the theme could not be
 * found.
 */
export function readPreference(key: string): string | null {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored !== null) return stored;
  } catch {
    // Disabled, or full. The cookie below is the second chance.
  }
  try {
    const value = new RegExp(`(?:^|; )${key}=([^;]*)`).exec(document.cookie)?.[1];
    return value === undefined ? null : decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * The three-tier read every per-browser preference makes, in the one order that
 * is correct: STORAGE FIRST, then what this page chose, then the default.
 *
 * THE ORDER MATTERS IN BOTH DIRECTIONS, which is why it is written down once
 * rather than at each preference.
 *
 * Storage stays authoritative while it works, so a newer choice another tab
 * wrote still wins at the next read here - `remembered` is a fallback, never an
 * override. And a value storage DID answer with is never overruled by
 * `remembered` even when it is junk: an unrecognised value is an ANSWER, and the
 * answer to one is the caller's default rather than the last thing this page
 * happened to choose. Only the key storage has nothing at all for reaches
 * `remembered`.
 *
 * `remembered` is the caller's own memory of a choice neither mirror accepted -
 * `theme.ts` and `voice.ts` each keep one and say at it what losing it costs.
 * Pass null for a preference with no such memory.
 */
export function readChoice<T extends string>(
  key: string,
  isValid: (value: unknown) => value is T,
  remembered: T | null,
  fallback: T,
): T {
  const stored = readPreference(key);
  if (isValid(stored)) return stored;
  if (stored === null && remembered !== null) return remembered;
  return fallback;
}

interface PreferenceOptions {
  secure?: boolean;
}

/**
 * Both mirrors, each attempted whatever the other did.
 *
 * BOUNDED VALUES ONLY - a fixed token like a theme or a wording, and never a
 * list that grows with what somebody makes. The cookie half is why: a cookie is
 * sent with EVERY request to this origin, every API call and every asset fetch,
 * for as long as it lives, and browsers reject one outright past a few
 * kilobytes - at which point the mirror silently stops mirroring and only the
 * larger value's owner ever finds out. An unbounded value belongs in
 * `localStorage` alone, read and written directly, which costs it Safari's
 * seven-day eviction of script-written storage and nothing else. Weigh that
 * against the request cost per preference: for the look it is worth it, because
 * the app comes back wrong without it. `notes/folded.ts` is the first value on
 * the other side of that line and says why at its own head.
 */
export function writePreference(key: string, value: string, options: PreferenceOptions = {}): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Full, or disabled. The cookie below still carries the choice, so a person
    // in private browsing keeps their preference for the session rather than
    // losing it on the next navigation.
  }
  try {
    const secure = options.secure === true ? '; Secure' : '';
    document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } catch {
    // jsdom without a document, and nothing else. Storage above already tried.
  }
}

export function clearPreference(key: string, options: PreferenceOptions = {}): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Disabled storage. The cookie below still gets its own attempt.
  }
  try {
    const secure = options.secure === true ? '; Secure' : '';
    document.cookie = `${key}=; path=/; max-age=0; SameSite=Lax${secure}`;
  } catch {
    // No document. Storage above already tried.
  }
}
